/**
 * Refer-a-friend page (OCP-383).
 *
 * Every portal customer gets one - unlike the Affiliate page (OCP-490),
 * this is NOT a granted capability, so there is no gating and no redirect:
 * the first load lazily mints the account's `?ref=` code server-side.
 *
 * Shows the ready-to-share link (copy button), the account's referred /
 * converted tally, and its earned give-get credit split into HELD (still
 * inside the refund window - not in the wallet yet) vs GRANTED (already
 * credited). The held/granted split is deliberately explicit: a referrer
 * who sees "10 EUR" must not think it is spendable before the refund
 * window closes (OCP-383 refund-hold, mirrors the affiliate commission).
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api } from '../lib/api';
import { fmtMoney, fmtNumber } from '../lib/format-money';
import { useTransientState } from '../lib/useTransientState';

interface ReferCredit {
  currency: string;
  heldCents: number;
  grantedCents: number;
}

interface Refer {
  code: string;
  shareLink: string;
  stats: { referred: number; converted: number };
  credit: ReferCredit[];
}

interface ReferWire {
  code: string;
  share_link: string;
  stats: { referred: number; converted: number };
  credit?: {
    currencies: Array<{ currency: string; held_cents: number; granted_cents: number }>;
  };
}

export function ReferPage() {
  const { t } = useTranslation('refer');
  const [refer, setRefer] = useState<Refer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useTransientState(false, 1500);

  const load = useCallback(async () => {
    try {
      const r = await api<ReferWire>('/v1/portal/refer');
      setRefer({
        code: r.code,
        shareLink: r.share_link,
        stats: r.stats,
        credit: (r.credit?.currencies ?? []).map((c) => ({
          currency: c.currency,
          heldCents: c.held_cents,
          grantedCents: c.granted_cents,
        })),
      });
    } catch (err) {
      setError(err instanceof ApiError ? t('loadFailedHttp', { status: err.status }) : t('loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function copy(link: string) {
    try {
      await navigator.clipboard?.writeText(link);
      setCopied(true);
    } catch {
      // Clipboard blocked (permissions / insecure context) - no-op.
    }
  }

  if (error) {
    return (
      <div
        role="alert"
        className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
      >
        {error}
      </div>
    );
  }

  if (!refer) {
    return <div className="text-sm text-gray-500">{t('loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('intro')}</p>
      </div>

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('shareLink')}</h2>
        <div className="mt-2 flex items-center gap-2">
          <code
            data-testid="refer-share-link"
            className="min-w-0 flex-1 truncate rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-2 py-1.5 text-xs"
          >
            {refer.shareLink}
          </code>
          <button
            type="button"
            onClick={() => void copy(refer.shareLink)}
            className="shrink-0 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            {copied ? t('copied') : t('copyLink')}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {t('yourCode')} <span className="font-mono">{refer.code}</span>
        </p>
      </section>

      <div className="grid grid-cols-2 gap-4">
        <StatTile label={t('referred')} value={refer.stats.referred} />
        <StatTile label={t('converted')} value={refer.stats.converted} />
      </div>

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('credit')}</h2>
        {refer.credit.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500" data-testid="refer-credit-empty">
            {t('creditEmpty')}
          </p>
        ) : (
          <div className="mt-3 space-y-3" data-testid="refer-credit">
            {refer.credit.map((c) => (
              <div key={c.currency} className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">{t('pending')}</div>
                  <div
                    data-testid={`refer-held-${c.currency}`}
                    className="mt-0.5 text-lg font-semibold text-gray-900 dark:text-gray-100"
                  >
                    {fmtMoney(c.heldCents, c.currency)}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">{t('inWallet')}</div>
                  <div
                    data-testid={`refer-granted-${c.currency}`}
                    className="mt-0.5 text-lg font-semibold text-emerald-700 dark:text-emerald-400"
                  >
                    {fmtMoney(c.grantedCents, c.currency)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-3 text-xs text-gray-500">
          {t('creditNote')}
        </p>
      </section>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">{fmtNumber(value)}</div>
    </div>
  );
}
